import {
  CONCURRENCE,
  URGENCY,
  concurrenceLabel,
  outcomeSubmissionSchema,
  urgencyLabel,
  type Concurrence,
  type Urgency,
} from "@so/shared";
import { useState } from "react";
import { useDocuments, useSubmitOutcome } from "../api/queries.js";
import { Alert, Button, Card, ErrorNote, Field, Select, Textarea, cx } from "./ui.js";

interface Action {
  action: string;
  rationale: string;
}

/**
 * Where the specialist writes the deliverable.
 *
 * Structured rather than a blank page on purpose: it is what lets the same
 * opinion be rendered consistently, summarised in an email, and - eventually -
 * counted, because "how often do our specialists disagree with the original
 * diagnosis" is a question worth being able to answer.
 */
export function OpinionForm({ appointmentId }: { readonly appointmentId: string }) {
  const submit = useSubmitOutcome(appointmentId);
  const { data: documents } = useDocuments(appointmentId);

  const [caseSummary, setCaseSummary] = useState("");
  const [concurrence, setConcurrence] = useState<Concurrence>("agree");
  const [rationale, setRationale] = useState("");
  const [findings, setFindings] = useState("");
  const [urgency, setUrgency] = useState<Urgency>("routine");
  const [actions, setActions] = useState<Action[]>([{ action: "", rationale: "" }]);
  const [investigations, setInvestigations] = useState("");
  const [reviewed, setReviewed] = useState<readonly string[]>([]);
  const [issues, setIssues] = useState<readonly string[]>([]);

  const needsRationale = concurrence !== "agree";

  function updateAction(index: number, patch: Partial<Action>) {
    setActions((current) =>
      current.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    );
  }

  function build() {
    return {
      case_summary: caseSummary.trim(),
      concurrence,
      ...(rationale.trim() === "" ? {} : { concurrence_rationale: rationale.trim() }),
      findings: findings.trim(),
      recommended_actions: actions
        .filter((a) => a.action.trim() !== "" || a.rationale.trim() !== "")
        .map((a) => ({ action: a.action.trim(), rationale: a.rationale.trim() })),
      additional_investigations: investigations
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line !== ""),
      urgency,
      reviewed_document_keys: [...reviewed],
    };
  }

  async function handleSubmit() {
    setIssues([]);

    // Validated with the same schema the handler uses, so the two cannot
    // disagree about what a complete opinion is.
    const parsed = outcomeSubmissionSchema.safeParse(build());

    if (!parsed.success) {
      setIssues(parsed.error.issues.map((issue) => issue.message));
      return;
    }

    await submit.mutateAsync(parsed.data);
  }

  const uploaded = (documents?.documents ?? []).filter((d) => d.uploaded);

  return (
    <Card className="p-5">
      <h2 className="text-[17px] font-semibold">Write the second opinion</h2>
      <p className="text-ink-soft mt-1 mb-5 text-[14px] leading-relaxed">
        This is what the patient receives. It is issued once and cannot be edited afterwards, so
        take a moment before submitting.
      </p>

      <div className="space-y-5">
        <Field
          label="Summary of the case as you understand it"
          required
          hint="Confirms to the patient that you have read their file."
        >
          <Textarea
            rows={4}
            value={caseSummary}
            onChange={(e) => setCaseSummary(e.target.value)}
            maxLength={4000}
          />
        </Field>

        <Field label="Do you agree with the original diagnosis?" required>
          <Select
            value={concurrence}
            onChange={(e) => setConcurrence(e.target.value as Concurrence)}
          >
            {CONCURRENCE.map((value) => (
              <option key={value} value={value}>
                {concurrenceLabel(value)}
              </option>
            ))}
          </Select>
        </Field>

        {/* Anything other than plain agreement has to be explained. An
            unexplained disagreement is worse than useless to a patient
            deciding whether to have surgery. */}
        {needsRationale && (
          <Field
            label="Why?"
            required
            hint="The patient may act on this. Explain your reasoning in full."
          >
            <Textarea
              rows={4}
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              maxLength={4000}
              className={cx(rationale.trim().length > 0 && rationale.trim().length < 20 && "border-warn")}
            />
          </Field>
        )}

        <Field label="Your findings" required>
          <Textarea
            rows={6}
            value={findings}
            onChange={(e) => setFindings(e.target.value)}
            maxLength={8000}
          />
        </Field>

        <fieldset>
          <legend className="mb-1.5 text-[14px] font-medium">
            What should the patient do next?
            <span className="text-ink-faint ml-1.5 text-xs font-normal">required</span>
          </legend>
          <p className="text-ink-faint mb-3 text-[13px]">
            At least one. This is the part patients act on.
          </p>

          <div className="space-y-3">
            {actions.map((item, index) => (
              <div key={index} className="border-line bg-surface-2 space-y-2 rounded-lg border p-3">
                <Textarea
                  rows={2}
                  placeholder="Recommended action"
                  value={item.action}
                  onChange={(e) => updateAction(index, { action: e.target.value })}
                  maxLength={300}
                />
                <Textarea
                  rows={2}
                  placeholder="Why you recommend it"
                  value={item.rationale}
                  onChange={(e) => updateAction(index, { rationale: e.target.value })}
                  maxLength={1000}
                />
                {actions.length > 1 && (
                  <Button
                    variant="ghost"
                    onClick={() => setActions((c) => c.filter((_, i) => i !== index))}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
          </div>

          {actions.length < 10 && (
            <Button
              variant="secondary"
              className="mt-3"
              onClick={() => setActions((c) => [...c, { action: "", rationale: "" }])}
            >
              Add another
            </Button>
          )}
        </fieldset>

        <Field
          label="Further investigations you would want"
          hint="One per line. Leave blank if none."
        >
          <Textarea
            rows={3}
            value={investigations}
            onChange={(e) => setInvestigations(e.target.value)}
            placeholder={"Bilateral breast MRI\nSentinel node assessment"}
          />
        </Field>

        <Field label="How soon should the patient act?" required>
          <Select value={urgency} onChange={(e) => setUrgency(e.target.value as Urgency)}>
            {URGENCY.map((value) => (
              <option key={value} value={value}>
                {urgencyLabel(value)}
              </option>
            ))}
          </Select>
        </Field>

        {uploaded.length > 0 && (
          <fieldset>
            <legend className="mb-1.5 text-[14px] font-medium">Records you reviewed</legend>
            <p className="text-ink-faint mb-2.5 text-[13px]">
              Recording what the opinion is based on is what makes it defensible later.
            </p>
            <div className="space-y-1.5">
              {uploaded.map((document) => (
                <label key={document.document_id} className="flex items-center gap-2.5 text-[14px]">
                  <input
                    type="checkbox"
                    className="accent-accent h-4 w-4"
                    checked={reviewed.includes(document.key)}
                    onChange={(e) =>
                      setReviewed((current) =>
                        e.target.checked
                          ? [...current, document.key]
                          : current.filter((key) => key !== document.key),
                      )
                    }
                  />
                  {document.filename}
                </label>
              ))}
            </div>
          </fieldset>
        )}

        {issues.length > 0 && (
          <Alert tone="warn" title="Not quite ready to send">
            <ul className="mt-1 list-disc space-y-0.5 pl-4">
              {issues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          </Alert>
        )}

        {submit.error !== null && <ErrorNote error={submit.error} />}

        {urgency === "urgent" && (
          <Alert tone="warn">
            Marking this urgent changes the patient&apos;s email to ask them to read it
            immediately.
          </Alert>
        )}

        <Button full loading={submit.isPending} onClick={() => void handleSubmit()}>
          Issue the second opinion
        </Button>
      </div>
    </Card>
  );
}
